/**
 * MessagingStatusBadge — one square chip for "what happened to this message".
 *
 * Reads either the router's uniform `messaging` stamp
 *   { channel: "whatsapp"|"sms", outcome, fallbackUsed, status?, fallbackReason?, error? }
 * or the legacy lead stamp `niv_call_requests.whatsapp` { status, errorMessage }.
 * Status palette: whatsapp / sms / fallback / failed / muted (see .css).
 * Used by NiV (Morning Briefing, messages tab, clinic detail) and later by the
 * therapist-facing read-only spots (whatsapp_integration.md §2.7).
 */
import { WhatsappLogo, ChatText, Warning, Prohibit } from "@phosphor-icons/react";
import "./MessagingStatusBadge.css";

const SKIP_REASONS = {
  skipped_quota: "מכסת ההודעות נגמרה",
  skipped_no_phone: "אין מספר טלפון",
  skipped_opt_out: "הנמען ביקש להסיר",
  skipped_disabled_by_niv: "סוג ההודעה כבוי",
  skipped_declined: "המטופל ביטל",
  skipped_inactive: "מטופל לא פעיל",
  skipped_past: "מועד שעבר",
  failed_no_link: "אין קישור",
  skipped: "לא נשלח",
};

const WA_STATE = { sent: "נשלח", delivered: "נמסר", read: "נקרא", failed: "נכשל" };

/** Normalises both stamp shapes into { tone, label, icon, title }. */
export function describeMessaging(stamp) {
  if (!stamp) return null;
  // Legacy lead stamp: { status }
  if (!stamp.channel && stamp.status) {
    const st = stamp.status;
    if (st === "failed") return { tone: "failed", label: "וואטסאפ נכשל", icon: "wa", title: stamp.errorMessage || "" };
    if (st === "skipped") return { tone: "muted", label: "ללא וואטסאפ", icon: "skip", title: stamp.errorMessage || "" };
    if (st === "sent_sms") return { tone: "fallback", label: "נשלח ב-SMS (גיבוי)", icon: "sms", title: "" };
    return { tone: "whatsapp", label: `וואטסאפ ${WA_STATE[st] || st}`, icon: "wa", title: "" };
  }
  const { channel, outcome, fallbackUsed, status, fallbackReason, error } = stamp;
  if (outcome === "failed_both" || outcome === "failed_provider") {
    return { tone: "failed", label: "לא נשלח", icon: "warn", title: error || fallbackReason || "" };
  }
  if ((outcome && outcome.startsWith("skipped")) || outcome === "failed_no_link") {
    return { tone: "muted", label: "דולג", icon: "skip", title: SKIP_REASONS[outcome] || outcome };
  }
  if (channel === "whatsapp") {
    const state = status && WA_STATE[status] ? WA_STATE[status] : "נשלח";
    if (status === "failed") return { tone: "failed", label: "וואטסאפ נכשל", icon: "wa", title: error || "" };
    return { tone: "whatsapp", label: `${state} בוואטסאפ`, icon: "wa", title: "" };
  }
  if (channel === "sms") {
    return fallbackUsed
      ? { tone: "fallback", label: "נשלח ב-SMS (גיבוי)", icon: "sms", title: fallbackReason ? `וואטסאפ לא עבד: ${fallbackReason}` : "" }
      : { tone: "sms", label: "נשלח ב-SMS", icon: "sms", title: "" };
  }
  return null;
}

const ICONS = {
  wa: WhatsappLogo,
  sms: ChatText,
  warn: Warning,
  skip: Prohibit,
};

export default function MessagingStatusBadge({ messaging, size = 11, className = "" }) {
  const d = describeMessaging(messaging);
  if (!d) return null;
  const Icon = ICONS[d.icon] || ChatText;
  return (
    <span className={`msg-badge msg-badge-${d.tone} ${className}`.trim()} title={d.title || undefined}>
      <Icon size={size} weight="fill" />
      {d.label}
    </span>
  );
}
